import TimeElapsed from '@components/TimeElapsed';
import { useLingui } from '@lingui/react';
import { Box, Typography } from '@mui/material';
import { type FC } from 'react';
import { getAttributes, getTextFormatters, NotificationData } from './utils';
import { useFormatters } from '@/hooks/useFormatters';
import { GREEN } from '@features/gateOperationModal/constants';

interface CardProps {
  data: NotificationData;
  isFeatured?: boolean;
}

export const NotificationCard: FC<CardProps> = ({ data, isFeatured = false }) => {
  const { i18n } = useLingui();
  const formatters = useFormatters();
  const { payload, type, timestamp } = data;

  const { Icon, bgcolor, fgcolor } = getAttributes(type);
  const { titleFormatter, contentFormatter } = getTextFormatters(type, i18n, formatters);

  const title = titleFormatter(payload, ({ children }) => (
    <span style={{ color: 'white' }}>{children}</span>
  ));
  const content = contentFormatter ? contentFormatter(payload) : null;

  const width = isFeatured ? '100%' : '90%';
  const minHeight = isFeatured ? 70 : 60;
  const marginX = isFeatured ? 0 : 'auto';
  const boxShadow = isFeatured ? '0px 4px 4px 0px #00000040' : 'none';

  const zIndex = isFeatured ? 2 : 1;
  const iconSize = 28;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: width,
        mx: marginX,
        mb: isFeatured ? 0.5 : 0,
        px: 3,
        py: 1,
        borderRadius: 2,
        bgcolor: bgcolor,
        color: fgcolor,
        boxShadow: boxShadow,
        position: 'relative',
        zIndex: zIndex,
        minHeight: minHeight,
        transition: 'all 0.15s ease-in-out',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
        <Box sx={{ display: 'flex', fontSize: iconSize }}>
          <Icon />
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography sx={{ fontWeight: 700, lineHeight: 1.2 }}>{title}</Typography>
          {content && <Typography sx={{ fontSize: '0.875rem' }}>{content}</Typography>}
        </Box>
      </Box>

      <Box sx={{ minWidth: '100px', textAlign: 'right', alignSelf: 'flex-start' }}>
        <Typography variant="caption">
          <TimeElapsed timestamp={timestamp} />
        </Typography>
      </Box>
    </Box>
  );
};
